import * as React from "react";
import moment from "moment";
import { mapNeurapulseFilterTypes } from "src/components/common/NeurapulseFilter";
import { getCareScore } from "src/common/Formatters";
import Utils from "src/common/utils";
import ClientAppConstants from "src/common/Constants";
import { AdminPermissions, ClientPermissions } from "src/common/Permissions";
import {
  hasAnyExceleRATEProduct,
  hasExceleRATEV1OnlyProduct,
  hasExceleRATEV2OnlyProduct,
  hasDriveABLEProduct,
} from "src/components/forms/ProductType";
import {
  IHeader,
  CoreHeaders,
  VitalsV1Headers,
  VitalsV2Headers,
  DCATHeaders,
  DoreHeaders,
  ExcelerateInfoHeaders,
  ExcelerateResultsHeaders,
  DriveABLEResultsHeaders,
  DriveABLEInfoHeaders,
  TripInspectionHeaders,
  NeurapulseHeaders,
} from "src/common/CsvExport";

export interface ICsvResult {
  [keys: string]: any;
  webId?: string;
  clientName?: string;
  firstName?: string;
  lastName?: string;
  employeeId?: string;
  dateCreated?: string;
  vitalsDateCompleted?: string;
  coreDateCompleted?: string;
  vitalsScore?: string | number;
  vitalsResult: string;
  cErrorResult: string;
  tErrorResult: string;
  skillsAppraisalResult: string;
  coreScore?: string | number;
  excelerateScore?: string | number;
  vehicleType?: string;
  courseName?: string;
  customFieldValues?: CustomFieldValueDTO[];
  vitalsComments?: string;
  vitalsEvaluator?: string;
  coreDateAdministered?: string;
  coreEvaluator?: string;
  coreComments?: string;
  careEvaluator?: string;
  careDateCompleted?: string;
  careScore?: number | string;
  careRecommendation?: string;
  careComments?: string;
  onroadRecommendation?: string;
  hasTripInspection?: string;
  commercialDrivingExp?: string;
  neurapulseState?: string;
  neurapulseCompleted?: string;
}

export interface IGetCsvDataResult {
  csvResults: ICsvResult[];
  headers: IHeader[];
}

export function sortByDate(
  resultA: ClientSiteAssessmentResultsDTO,
  resultB: ClientSiteAssessmentResultsDTO
): number {
  return resultA.dateCompleted! > resultB.dateCompleted! ? 1 : -1;
}

export function isEmptyOrUndefined(value: any): boolean {
  return value === undefined || value.length === 0;
}

export function setCsvResult(statusId?: number | string): string {
  switch (statusId) {
    case 0: return "Fail";
    case 1: return "Borderline";
    case 2: return "Pass";
    case 3: return "Hazardous";
    case 4: return "Aborted";
    default: return "-";
  }
}

export function getVitalsCsvResult(
  vitalsScore: any,
  vitalsDateCompleted: any,
  vitalsTestId: any
): string | number {
  if (vitalsScore) return vitalsScore;
  if (vitalsDateCompleted) return "C";
  if (!vitalsTestId) return "-";
  return "NC";
}

export function canSeeScores(currentUser: any, datasetPermissions: any): boolean {
  if (
    currentUser.permissions!.includes(AdminPermissions.GlobalAdmin) ||
    (datasetPermissions &&
      datasetPermissions.permissions &&
      (datasetPermissions.permissions.includes(ClientPermissions.ViewAllResults) ||
        datasetPermissions.permissions.includes(ClientPermissions.ViewOwnResults)))
  ) {
    return true;
  }
  return false;
}

export function getDatasetPermissions(currentUser: any, datasetId: number): any {
  return currentUser.datasetPermissions!.find(
    (x: any) => x.datasetId === Number(datasetId)
  );
}

export function returnedAssessmentCustomFields(
  customFields: CustomFieldDTO[],
  pickListItems: PickListItemResponseDTO[],
  datasetId: number
): CustomFieldDTO[] {
  const fieldTypes: number[] = pickListItems
    .filter(
      (p: PickListItemResponseDTO) =>
        p.code === "AssessmentType" ||
        p.code === "CoreType" ||
        p.code === "VitalsType" ||
        p.code === "CareType"
    )
    .map((foundType: PickListItemResponseDTO) => foundType.id!);

  return customFields.filter(
    (cf) =>
      cf.isActive === true &&
      cf.showInListView === true &&
      fieldTypes.includes(cf.customFieldType!) &&
      cf.datasetIds!.includes(+datasetId)
  );
}

/**
 * Maps assessment results to CSV rows + headers.
 * Pass ResultsHeaders (dataset view) or ClientResultsHeaders (client view) as baseHeaders.
 * includeClientNameFields: true for dataset view (includes clientName, firstName, lastName).
 */
export function getCsvData(
  data: any[],
  baseHeaders: IHeader[],
  dataset: DatasetDTO,
  includeClientNameFields: boolean
): IGetCsvDataResult {
  if (!data) return { headers: [], csvResults: [] };

  const csvResults: ICsvResult[] = data.map((r) => {
    const source: ClientSiteAssessmentResultsDTO = r._original ? r._original : r;
    const row: ICsvResult = {
      webId: source.webId,
      dateCreated: source.dateCreated
        ? moment.utc(source.dateCreated).local().format(ClientAppConstants.DefaultDateFormat)
        : "-",
      vitalsScore: getVitalsCsvResult(
        source.vitalsScore,
        source.vitalsDateCompleted,
        source.vitalsTestResultId
      ),
      vitalsComments: source.vitalsComments ? source.vitalsComments : "-",
      vitalsEvaluator: source.vitalsEvaluator ? source.vitalsEvaluator : "-",
      vitalsDateCompleted: source.vitalsDateCompleted
        ? moment.utc(source.vitalsDateCompleted).local().format(ClientAppConstants.DefaultDateFormat)
        : "-",
      vitalsResult: source.vitalsStatusId ? (source.vitalsStatusId as any).pascalCase() : "-",
      cErrorResult: source.cErrorStatusId ? (source.cErrorStatusId as any).pascalCase() : "-",
      tErrorResult: source.tErrorStatusId ? (source.tErrorStatusId as any).pascalCase() : "-",
      skillsAppraisalResult: source.appraisalStatusId ? (source.appraisalStatusId as any).pascalCase() : "-",
      coreScore: source.coreScore !== undefined ? source.coreScore : "-",
      excelerateScore: source.excelerateScore !== undefined ? source.excelerateScore : "NC",
      vehicleType: source.vehicleType,
      courseName: source.courseName,
      customFieldValues: source.customFieldValues,
      coreDateAdministered: source.coreDateAdministered
        ? moment.utc(source.coreDateAdministered).format(ClientAppConstants.DefaultDateFormat)
        : "-",
      coreDateCompleted: source.coreDateCompleted
        ? moment.utc(source.coreDateCompleted).format(ClientAppConstants.DefaultDateFormat)
        : "-",
      coreEvaluator: source.coreEvaluator,
      coreComments: source.coreComments ? Utils.formatComment(source.coreComments) : "-",
      careEvaluator: source.careEvaluator ? source.careEvaluator : "-",
      careDateCompleted: source.careDateCompleted
        ? moment.utc(source.careDateCompleted).format(ClientAppConstants.DefaultDateFormat)
        : "-",
      careScore: getCareScore(source.careScore, source.careDateCompleted, source.careTestResultId),
      careRecommendation: source.careRecommendation ? (source.careRecommendation as any).pascalCase() : "-",
      careComments: source.careComments ? Utils.formatComment(source.careComments) : "-",
      onroadRecommendation: source.onroadRecommendation ? (source.onroadRecommendation as any).pascalCase() : "-",
      hasTripInspection: source.hasTripInspection === true ? "true" : "false",
      commercialDrivingExp:
        source.monthsCommercialDrivingExp !== undefined
          ? Utils.getCommercialDrivingExpDesc(source.monthsCommercialDrivingExp)
          : "",
      employeeId: source.employeeId,
      neurapulseState: source.neurapulseState
        ? mapNeurapulseFilterTypes(source.neurapulseState)
        : "-",
      neurapulseCompleted: source.neurapulseCompleted
        ? moment.utc(source.neurapulseCompleted).local().format(ClientAppConstants.DefaultDateFormat)
        : "-",
    };

    if (includeClientNameFields) {
      row.clientName = source.clientName;
      row.firstName = source.firstName;
      row.lastName = source.lastName;
    }

    return row;
  });

  let headers = [...baseHeaders];

  if (hasAnyExceleRATEProduct(dataset.productId)) {
    if (dataset.showClientParticipantId === true) headers.push(...ExcelerateInfoHeaders);
    headers.push(...ExcelerateResultsHeaders);
    headers.push(...CoreHeaders);
  }
  if (hasExceleRATEV2OnlyProduct(dataset.productId)) {
    headers.push(...VitalsV2Headers);
    headers.push(...TripInspectionHeaders);
  }
  if (hasExceleRATEV1OnlyProduct(dataset.productId)) {
    headers.push(...VitalsV1Headers);
  }
  if (hasDriveABLEProduct(dataset.productId)) {
    if (dataset.showClientParticipantId === true) headers.push(...DriveABLEInfoHeaders);
    headers.push(...DriveABLEResultsHeaders);
    headers.push(...DoreHeaders);
    headers.push(...DCATHeaders);
  }
  if (dataset.neurapulseProjectId) {
    headers.push(...NeurapulseHeaders);
  }

  csvResults.forEach((e: ICsvResult) => {
    const customFieldValues = e.customFieldValues ? e.customFieldValues : [];
    customFieldValues.forEach((customField: any) => {
      const keyName = customField.name ? customField.name : "";
      const value = customField.value ? customField.value : "";
      e[keyName] = value;
      if (!headers.some((h) => h.label === keyName)) {
        headers.push({ label: keyName, key: keyName });
      }
    });
    delete e.customFieldValues;
  });

  return { headers, csvResults };
}

/**
 * Renders multiple custom field label values separated by commas.
 * Used in getColumns cell renderers.
 */
export function setMultipleLabelComponent(values: string[]): React.ReactElement {
  return React.createElement(
    "span",
    null,
    values.map((v, i) =>
      React.createElement("span", { key: i }, i > 0 ? `, ${v}` : v)
    )
  );
}
